/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import classNames from 'classnames';
import { t } from 'i18next';

// Component imports
import { useState, useMemo } from 'react';
import { Header } from 'antd/lib/layout/layout';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Grid, Row, Col, Button, Dropdown, Menu } from 'antd';
import AppLogo from '../../assets/logo.svg';
import AppLogoFull from '../../assets/logo-full.svg';
import { BankOutlined, LogoutOutlined, UserOutlined, SettingOutlined, QuestionCircleOutlined, InfoCircleOutlined, ControlOutlined, LinkOutlined, RobotOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';

// Misc imports
import { NAV_ROOT, NAV_LOGOUT } from '../../constants/navigation';
import { HRWIZ_TABS } from '../../screens/HRWizConsoleScreen';
import { useFetchBotsQuery } from '../../store/bots/botsApi';
import { IS_HRWIZ_PREFERENCES_BUTTON_DISABLED } from '../../constants/env';

// Constant declarations
const baseClassName = 'global-header';

const HRWizHeader = () => {
  const screens = Grid.useBreakpoint();
  const isLargeScreenSize = screens.lg;
  const navigate = useNavigate();
  const { orgId, tenantId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useSelector((state) => state.auth);
  const { data: bots } = useFetchBotsQuery({ orgId }, { skip: !orgId });

  const orgs = useMemo(() => {
    const _orgs = [...currentUser.organizations || []];
    return _orgs.sort((a, b) => a.displayName?.localeCompare(b.displayName));
  }, [currentUser]);

  const [dropdownVisible, setDropdownVisible] = useState(null); // null | bots | help | settings | profile

  const handleDropdownVisibleChange = (dropdown, visible) => {
    setDropdownVisible(visible ? dropdown : null);
  };

  const handleMenuClick = ({ key }) => {
    switch (key) {
      case 'logout':
        navigate(NAV_LOGOUT);
        break;
      case 'profile':
        // TODO: navigate to the user's profile page
        break;
      case 'settings':
        // TODO: navigate to the app's settings page
        break;
      case 'notifications':
        // TODO: navigate to the user's notifications page
        break;
      case 'help':
        // TODO: navigate to the app's help page
        window.location.href = 'https://helvia.ai/about';
        break;
      case 'about':
        // TODO: navigate to the app's help page
        window.location.href = 'https://helvia.ai/about';
        break;
      case 'preferences':
        setSearchParams({ ...Object.fromEntries(searchParams.entries()), tab: HRWIZ_TABS.PREFERENCES });
        break;
      case 'sources':
        setSearchParams({ ...Object.fromEntries(searchParams.entries()), tab: HRWIZ_TABS.SOURCES });
        break;
      default:
        if (key?.includes('###')) {
          const parts = key.split('###');
          const _orgId = parts[0];
          const botId = parts[1];
          navigate(`/org/${_orgId}/bots/${botId}`);
        } else {
          navigate(`/org/${key}`);
        }
        break;
    }
    setDropdownVisible(null);
  };

  const orgsMenu = useMemo(() => {
    const items = orgs
      .map((_org) => ({
        key: `${_org.id}`,
        label: <span className="text-black fw-bold">{_org.displayName}</span>
      }));

    return (
      <Menu onClick={handleMenuClick} items={items} selectedKeys={[orgId]} className="overflow-y-auto" style={{ maxHeight: '600px' }} />
    );
  }, [orgs, orgId]);

  const botsMenu = useMemo(() => {
    const items = bots?.items?.map((_bot) => ({
      key: `${orgId}###${_bot.id}`,
      label: <span>{_bot.displayName}</span>
    }));

    return (
      <Menu onClick={handleMenuClick} items={items} selectedKeys={[`${orgId}###${tenantId}`]} className="overflow-y-auto" style={{ maxHeight: '600px' }} />
    );
  }, [bots, orgId, tenantId]);

  return (
    <Header className={baseClassName + ' border-bottom'}>
      <Row justify={'space-between'}>
        <Col>
          <Link
            to={NAV_ROOT}
            className={`${baseClassName}__logo border-0`}
          >
            <div
              className={classNames(`${baseClassName}__logo__image`, {
                'px-3': !isLargeScreenSize
              })}
            >
              <img
                src={isLargeScreenSize ? AppLogoFull : AppLogo}
                alt="logo"
              />
            </div>
          </Link>
        </Col>
        <Col className="mr-3">
          {orgs && orgs.length > 1 && (
            <Dropdown
              overlay={orgsMenu}
              trigger={['click']}
              visible={dropdownVisible === 'orgs'}
              onVisibleChange={visible => handleDropdownVisibleChange('orgs', visible)}
            >
              <Button
                type="ghost"
                className="border-0 mr-3"
                icon={<BankOutlined />}
              />
            </Dropdown>
          )}
          {bots?.items && (
            <Dropdown
              overlay={botsMenu}
              trigger={['click']}
              visible={dropdownVisible === 'bots'}
              onVisibleChange={visible => handleDropdownVisibleChange('bots', visible)}
            >
              <Button
                type="ghost"
                className="border-0 mr-3"
                icon={<RobotOutlined />}
              />
            </Dropdown>
          )}
          <Dropdown
            overlay={(
              <Menu onClick={handleMenuClick}>
                <Menu.Item key="help" icon={<QuestionCircleOutlined />}>
                  {t('Help')}
                </Menu.Item>
                <Menu.Item key="about" icon={<InfoCircleOutlined />}>
                  {t('About')}
                </Menu.Item>
              </Menu>
            )}
            trigger={['click']}
            visible={dropdownVisible === 'help'}
            onVisibleChange={visible => handleDropdownVisibleChange('help', visible)}
          >
            <Button
              type="ghost"
              className="border-0 mr-3"
              icon={<QuestionCircleOutlined />}
            />
          </Dropdown>
          <Dropdown
            overlay={(
              <Menu onClick={handleMenuClick}>
                {
                  IS_HRWIZ_PREFERENCES_BUTTON_DISABLED ? null : (
                    <Menu.Item key="preferences" icon={<ControlOutlined />}>
                      {t('Preferences')}
                    </Menu.Item>
                  )
                }
                <Menu.Item key="sources" icon={<LinkOutlined />}>
                  {t('Sources')}
                </Menu.Item>
              </Menu>
            )}
            trigger={['click']}
            visible={dropdownVisible === 'settings'}
            onVisibleChange={visible => handleDropdownVisibleChange('settings', visible)}
          >
            <Button
              type="ghost"
              className="border-0 mr-3"
              icon={<SettingOutlined />}
            />
          </Dropdown>
          <Dropdown
            overlay={(
              <Menu onClick={handleMenuClick}>
                <Menu.Item key="logout" icon={<LogoutOutlined />}>
                  {t('Log Out')}
                </Menu.Item>
              </Menu>
            )}
            trigger={['click']}
            visible={dropdownVisible === 'profile'}
            onVisibleChange={visible => handleDropdownVisibleChange('profile', visible)}
          >
            <Button
              type="ghost"
              className="border-0 mr-3"
              icon={<UserOutlined />}
            />
          </Dropdown>
        </Col>
      </Row>
    </Header>
  );
};

export default HRWizHeader;
